import { Platform, TouchableOpacity, View, Image } from "react-native";
import React, { ReactNode, useEffect } from "react";
import { StyleSheet, Text } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useGlobalState } from "../../shared/state";
import BackButton from "../Buttons/back-button/BackButton";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import { Fonts } from "../../styles";
import * as _ from "lodash";
import OfflineNotice from "../Header/OfflineNotice";
import { deleteCarts } from "../../screens/Order/cartFunctions";
import { BirkenstockLogo, Icons } from "../../assets";

type Props = {
  children?: ReactNode;
  showHeaderCloseButton: boolean;
  showHeaderLogo?: boolean;
  showMenuButtons?: boolean;
  showHomeButton?: boolean;
  showStartButton?: boolean;
  showBackButton?: boolean;
  showLanguageButton?: boolean;
  hideLeftButton?: boolean;
  hideNewScanButton?: boolean;
  hideInfoButton?: boolean;
  hideBackButton?: boolean;
  title?: string | undefined;
  subtitle?: string | undefined;
  showBirkenstockFooter?: boolean;
  showBirkenstockHomeButton?: boolean;
  showBirkenstockFooterLogo?: boolean;
  isHomeScreen?: boolean;
};

const LayoutWrapper: React.FC<Props> = ({
  children,
  showHeaderCloseButton,
  showHeaderLogo,
  showMenuButtons = true,
  showStartButton = false,
  showBackButton = false,
  showHomeButton = false,
  showLanguageButton = false,
  hideLeftButton = false,
  hideNewScanButton = false,
  hideInfoButton = false,
  hideBackButton = false,
  title = undefined,
  subtitle = undefined,
  showBirkenstockFooter = false,
  showBirkenstockHomeButton = false,
  showBirkenstockFooterLogo = false,
  isHomeScreen = false,
}: Props): JSX.Element => {
  const [currentScanner] = useGlobalState("currentScanner");
  const [isUserActive] = useGlobalState("isUserActive");
  const [currentScreen, setCurrentScreen] = useGlobalState("currentScreen");
  const [, setCurrentUser] = useGlobalState("currentUser");
  const [currentMeasurements, setCurrentMeasurements] = useGlobalState("currentMeasurements");
  const [, setMatchedProducts] = useGlobalState("matchedProducts");
  const [, setInitialMatchedProducts] = useGlobalState("initialMatchedProducts");
  const [, setAllProducts] = useGlobalState("allProducts");
  const [, setCurrentScan] = useGlobalState("currentScan");
  const [, setUserToken] = useGlobalState("userToken");
  const [carts, setCarts] = useGlobalState("carts");

  const [modalIsDisplayed, setModalIsDisplayed] = useGlobalState("modalIsDisplayed");
  const buttonConfig = { showStartButton, showHomeButton, showMenuButtons, showLanguageButton, hideLeftButton, hideNewScanButton, showBackButton };
  const navigation = useNavigation();
  const routeIndex = navigation.getState().index;
  const routeName = navigation.getState().routes[routeIndex].name;
  const headerProps = {
    closeButton: showHeaderCloseButton,
    logo: showHeaderLogo,
    hideInfoButton: hideInfoButton,
    hideBackButton: hideBackButton,
  };

  const isBirkenstock = currentScanner && currentScanner.partner && currentScanner.partner?.toLowerCase() === "birkenstock";

  const doLogout = () => {
    setCurrentUser(undefined);
    setUserToken(undefined);
    setCurrentMeasurements(undefined);
    setMatchedProducts(undefined);
    setInitialMatchedProducts(undefined);
    setCurrentScan(undefined);
    setAllProducts([]);
    deleteCarts(carts);
    setCarts([]);
    setCurrentScreen("Home");
    navigation.navigate("Home");
  };

  useEffect(() => {
    if (typeof routeName === "string" && routeName.includes("Modal")) {
      setModalIsDisplayed(true);
    } else {
      setModalIsDisplayed(false);
    }
  }, [routeName]);

  useEffect(() => {
    if (!isUserActive && currentMeasurements) doLogout();
  }, [isUserActive]);

  const justifyElements = () => {
    if (showBirkenstockHomeButton && !showBirkenstockFooterLogo) {
      return "center";
    } else if (!showBirkenstockHomeButton && showBirkenstockFooterLogo) {
      return "flex-end";
    } else {
      return "space-between";
    }
  };

  const textStyle = (size: number, fontFamily: string) => {
    return { fontFamily, color: "#0e2288", fontSize: size };
  };

  return (
    <View style={[styles.container, { backgroundColor: isHomeScreen ? undefined : "#e6e7e8" }]}>
      <OfflineNotice />
      <Header {...headerProps} />
      <View style={styles.content}>
        {title && <Text style={[Fonts.pageTitle, isBirkenstock && textStyle(48, "Birkentype")]}>{modalIsDisplayed ? " " : title}</Text>}
        {subtitle && <Text style={[Fonts.pageSubTitle, isBirkenstock && textStyle(22, "Geist-Light")]}>{modalIsDisplayed ? " " : subtitle}</Text>}
        {showBackButton && (
          <View style={styles.backButtonWrapper}>
            <BackButton />
          </View>
        )}
        {children}
      </View>
      {isBirkenstock && showBirkenstockFooter && (
        <View
          style={[
            styles.birkenstockFooter,
            {
              justifyContent: justifyElements(),
            },
          ]}
        >
          {showBirkenstockHomeButton && (
            <TouchableOpacity
              style={[
                styles.button,
                {
                  width: "5%",
                  height: "65%",
                  marginLeft: showBirkenstockFooterLogo ? "5%" : 0,
                  backgroundColor: "white",
                  borderRadius: 10,
                },
              ]}
              onPress={() => navigation.navigate("Home")}
            >
              <Image
                source={Icons.homeIcon}
                style={{
                  width: "100%",
                  height: "100%",
                  resizeMode: "contain",
                  borderRadius: 10,
                }}
              />
            </TouchableOpacity>
          )}

          {showBirkenstockFooterLogo && (
            <View
              style={{
                width: "15%",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <Image
                source={BirkenstockLogo}
                style={{
                  width: "100%",
                  height: "25%",
                  resizeMode: "contain",
                }}
              />
            </View>
          )}
        </View>
      )}
      {!isBirkenstock && <Footer {...buttonConfig} />}
    </View>
  );
};

export default LayoutWrapper;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: "1%",
  },
  content: {
    flex: 1,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    flexDirection: "column",
    justifyContent: "space-between",
    paddingLeft: "1%",
    paddingRight: "1%",
  },
  birkenstockFooter: {
    height: "13%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "center",
    alignItems: "center",
  },
  backButtonWrapper: {
    height: Platform.OS === "web" ? "12.5%" : "2.5%",
    width: "5.8%",
    position: "absolute",
    zIndex: 2,
    marginTop: Platform.OS === "web" ? "5.5%" : "5.5%",
    marginLeft: "1.6%",
  },
  button: {
    padding: 12,
    borderRadius: 6,
    backgroundColor: "#fff",
    boxShadow: "0px 4px 6px #00000029",
    height: "25%",
  },
});
